import CopyIcon from '@src/svgs/CopyIcon';
import GenerateIcon from '@src/svgs/CreateIcon';
import CreateIcon from '@src/svgs/DocumentIcon';
import DownloadIconSmall from '@src/svgs/DownloadIconSmall';
import RotatingSquare from '@src/svgs/RotatingSquare';
import SaveIcon from '@src/svgs/SaveIcon';
import ShareIcon from '@src/svgs/ShareIcon';
import styled, { css } from 'styled-components';

type Props = {
  disabled?: boolean;
  onClick?: () => void;
  buttonLabel?: string;
  variant?: 'primary' | 'outlined';
  size?: 'xs' | 'sm' | 'lg';
  fullWidth?: boolean;
  icon?:
    | 'create'
    | 'generate'
    | 'regenerate'
    | 'copy'
    | 'share'
    | 'save'
    | 'download';
  'data-testid'?: string;
};

const ContentButton = (props: Props) => {
  const {
    disabled,
    onClick,
    variant = 'primary',
    size = 'sm',
    fullWidth = false,
    buttonLabel = 'Generate',
    icon: _icon,
  } = props;
  const iconSize = size === 'xs' ? '14' : '16';
  const iconProps = {
    width: iconSize,
    height: iconSize,
    // Match the label color so icon + text read as one chip.
    // Primary → white (on green), Secondary → foreground (on transparent).
    strokeColor:
      variant === 'primary' ? 'var(--background)' : 'var(--foreground)',
  };
  const icon =
    _icon === 'create' ? (
      <CreateIcon {...iconProps} />
    ) : _icon === 'generate' ? (
      <GenerateIcon {...iconProps} />
    ) : _icon === 'regenerate' ? (
      <RotatingSquare {...iconProps} />
    ) : _icon === 'copy' ? (
      <CopyIcon {...iconProps} />
    ) : _icon === 'share' ? (
      <ShareIcon {...iconProps} />
    ) : _icon === 'save' ? (
      <SaveIcon {...iconProps} />
    ) : _icon === 'download' ? (
      <DownloadIconSmall {...iconProps} />
    ) : null;
  return (
    <Button
      type="button"
      disabled={disabled}
      onClick={onClick}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      data-testid={props['data-testid']}
    >
      {icon}
      {buttonLabel}
    </Button>
  );
};

const Button = styled.button<{
  variant: 'primary' | 'outlined';
  size: 'xs' | 'sm' | 'lg';
  fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ size }) => (size === 'xs' ? '6px' : '8px')};
  height: ${({ size }) =>
    size === 'lg'
      ? 'var(--btn-h-xl, 48px)'
      : size === 'xs'
        ? 'var(--btn-h-sm, 32px)'
        : 'var(--btn-h-md, 36px)'};
  padding: 0
    ${({ size }) =>
      size === 'lg'
        ? '16px'
        : size === 'xs'
          ? 'var(--btn-px-sm, 12px)'
          : 'var(--btn-px-md, 14px)'};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  white-space: nowrap;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  font-size: ${({ size }) =>
    size === 'lg'
      ? 'var(--text-md, 16px)'
      : size === 'xs'
        ? 'var(--text-xs, 12px)'
        : 'var(--text-sm, 14px)'};
  line-height: 1;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;

  ${({ variant }) =>
    variant === 'outlined'
      ? css`
          background: transparent;
          color: var(--foreground);
          border: 1px solid var(--border);
          font-weight: var(--font-weight-medium, 500);

          &:hover:not(:disabled) {
            background: var(--surface-elevated);
            border-color: var(--text-muted);
          }

          &:active:not(:disabled) {
            background: var(--surface-elevated);
            transform: translateY(0.5px);
          }

          &:focus-visible {
            box-shadow:
              0 0 0 2px var(--background),
              0 0 0 4px var(--primary);
          }
        `
      : css`
          /* Match the Produce Clips button style — filled green with a 1px
             matching border and bold-700 label. Hover / active / focus states
             are layered on top so this variant behaves consistently across
             the app wherever ContentButton variant="primary" is used. */
          background: var(--primary);
          color: var(--background);
          border: 1px solid var(--primary);
          font-weight: 700;

          &:hover:not(:disabled) {
            background: var(--primary-hover, var(--primary));
            border-color: var(--primary-hover, var(--primary));
          }

          &:active:not(:disabled) {
            background: var(
              --primary-pressed,
              var(--primary-hover, var(--primary))
            );
            border-color: var(
              --primary-pressed,
              var(--primary-hover, var(--primary))
            );
            transform: translateY(0.5px);
          }

          &:focus-visible {
            box-shadow:
              0 0 0 2px var(--background),
              0 0 0 4px var(--primary-hover, var(--primary));
          }
        `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default ContentButton;
